import PiwikPro from '@piwikpro/react-piwik-pro';
import { get } from 'lodash/fp';
import {
    isInternal,
    isLocalDNSMapping,
    isLocalhost,
    isProduction,
} from 'services/configService';
import CountryStore from 'stores/CountryStore';
import { getCurrentActiveView } from './generalUtils';
import { VIEW } from 'interfaces';
import HeatmapStore from 'stores/HeatmapStore';

const SOURCE = "BMI";
// TODO: Docs suggest that Custom dimension 1 should always be Source, but I already created it with a different id.
// https://fitchgroup.atlassian.net/wiki/spaces/FC/pages/819757444/Piwik+Overview+Testing+Details
const SOURCE_DIM_ID = 5; 

const PIWIK_CONFIG = {
    prod: '6ca382e3-57a9-4d75-8b1e-4550c1bbcbb0',
    staging: '67f92d52-dd5b-49e4-9fac-8400fa29b201',
};

const PIWIK_CONTAINER_URL = 'https://purple.fitchconnect.com';

export enum SESSION_DIMENSIONS {
    WEB_USER_ID = '2',
    SESSION_ID = '3',
    REQUEST_ID = '4',
    ENTITY_ID = '6'
}
export enum EVENT_DIMENSIONS {
    SELECTED_COUNTRIES = '1'
}
const shouldSendPiwik = !isLocalhost() && !isInternal() && !isLocalDNSMapping();

export const initPiwik = () => {
    if (!shouldSendPiwik) {
        return;
    }

    const piwikKey = isProduction() ? PIWIK_CONFIG.prod : PIWIK_CONFIG.staging;
    PiwikPro.initialize(piwikKey, PIWIK_CONTAINER_URL);
};

export const initUserId = (webUserId: string | number) => {
    if (!shouldSendPiwik) {
        return;
    }
    try {
        if (!window['_paq']) {
            throw Error('Piwik tracking is not properly initialized');
        }
        if (typeof webUserId !== 'string' && typeof webUserId !== 'number') {
            throw Error(
                `shared-ui-piwik: setUserId() must be called with a string userId parameter.`
            );
        }
        const item = ['setUserId', String(webUserId)];
        window['_paq'].push(item);
        window['_paq'].push(['setCustomDimensionValue', SOURCE_DIM_ID, SOURCE]);
        window['_paq'].push(['setCustomDimensionValue', SESSION_DIMENSIONS.WEB_USER_ID, String(webUserId)]);
        window['_paq'].push(['setCustomDimensionValue', SESSION_DIMENSIONS.SESSION_ID, ""]);
        window['_paq'].push(['setCustomDimensionValue', SESSION_DIMENSIONS.REQUEST_ID, ""]);
        window['_paq'].push(['setCustomDimensionValue', SESSION_DIMENSIONS.ENTITY_ID, ""]);
    } catch (error) {
        console.error(`Error reporting to Piwik: ${get('message', error)}`);
    }
};

export const trackPageView = (url: string) => {
    if (!shouldSendPiwik) {
        return;
    }
    try {
        window['_paq'].push(['setCustomUrl', url]);
        window['_paq'].push(['trackPageView']);
    } catch (error) {
        console.error(`Error tracking page view: ${get('message', error)}`);
    }
};

export const trackCustomEvent = (eventCategory: string, eventAction: string, eventName: string, eventValue: number = 1) => {
    if (!shouldSendPiwik) {
        return;
    }
    window['_paq'].push(['setCustomDimensionValue', EVENT_DIMENSIONS.SELECTED_COUNTRIES, getCountryList()]);
    try {
        window['_paq'].push(['trackEvent', eventCategory, eventAction, eventName, eventValue])
    } catch (error) {
        console.error(`Error tracking button click: ${get('message', error)}`);
    }
};


export const clearUserId = () => {
    if (!shouldSendPiwik) {
        return;
    }
    try {
        window['_paq'].push(['resetUserId']);
    } catch (error) {
        console.error(`Error clearing Piwik: ${get('message', error)}`);
    }
};

export const getCountryList = () => {
    const activeView = getCurrentActiveView();
    const countryIds = activeView === VIEW.HEATMAP ? HeatmapStore.countriesList: CountryStore.currentCountryList;
    return countryIds.map(cId => CountryStore.countries.allCountries[cId].name)
}
